import './index.css';
import { z } from 'zod';
import { createRenderer } from './render/app';
import { createInputProfile, setTraitsSource } from './render/input';
import { computeLayout, sameLayout, type LayoutProfile } from './render/layout';
import { readSafeAreaInsets } from './ui/safeArea';
import { bindKeyboard } from './render/keyboard';
import { KeyRouter } from './ui/keys';
import { createLocalStore, createSettingsController, displayNickname } from './ui/settings';
import { detectLang, setLang, t } from './i18n';
import { createRoomPresetStore } from './ui/roomPreset';
import { urlWithoutHash } from './ui/room';
import { normalizePath, parseRoute, routePathFor, type Route } from './ui/router';
import { createRoomController, type AudioSink, type RoomController } from './ui/session';
import { connectRoom, type OnlineRoom } from './host/online';
import { probeRelays, relaySocketStates, relayUrlsForRoom, type TurnServerConfig } from './net/trystero';
import { createBgmController, windowGestureSource, PRE_GAME_BGM_PARAMS } from './ui/bgm';
import { createZenithBgmPort } from './audio/port';
import { createImpactSfx } from './audio/sfx';
import {
  createEnergyTracker,
  toBgmParams,
  type EnergyReporter,
  type EnergySnapshot,
} from './audio/energy';
import type { ZenithBgmParams } from './audio/score.zenith';
import { createBgmOverride, installDevTools } from './dev/tools';
import { currentGame } from './dev/registry';
import type { GameAction } from './core/actions';
import { createMusicClock } from './ui/beat';
import { createScreenHost } from './ui/screenHost';
import {
  IDLE_CONNECT,
  offlineReason,
  reduceConnect,
  type ConnectMode,
  type ConnectState,
} from './ui/connect';
import { SCREEN, OVERLAY } from './ui/screens/ids';
import { createTitleScreen } from './ui/screens/title';
import { createMeetupScreen } from './ui/screens/meetup';
import { createConnectingScreen } from './ui/screens/connecting';
import { createRoomScreen } from './ui/screens/room';
import { createSettingsScreen } from './ui/screens/settings';
import { createHotseatScreen, createOnlineScreen } from './ui/screens/game';
import { createCreditScreen } from './ui/screens/credit';
import { createChangelogScreen } from './ui/screens/changelog';
import { ALL_PACKS, defaultRegistry } from './packs';
import { packHash } from './core/pack';

/**
 * **游戏入口。**
 *
 * 它只做四件事，一件也不多做：
 *
 * 1. 起 Pixi（全项目唯一一处）、把指针收在**舞台**上
 * 2. 起设置 / 房间 / 音量这三样"会话状态"
 * 3. 把四块屏幕注册进菜单宿主，然后停在标题页
 * 4. 把那唯一的 ticker 接到宿主上
 *
 * ## 和上一版入口的关系
 *
 * 上一版 `main.ts` 是一块**落地屏**（说明文字 + 指向调试页的链接）。
 * 现在它是真正的入口：菜单系统上线了。**游戏界面本身仍然没做** ——
 * 那是下一步，所以标题页上的「本地对局」是明确禁用的。
 *
 * ## ⚠️ 这里没有 DOM 界面
 *
 * 只有**一个** `#pixi-root` 容器给 canvas 用。菜单、面板、按钮、滑条、输入框
 * 全部画在 Pixi 里（`architecture.md` §7.6）。连昵称输入框都是自己接
 * `keydown` 拼出来的 —— 页面上没有任何可编辑元素，所以输入法不会被激活。
 * 唯一的例外是"下载文件"，理由写在 `ui/transfer.ts` 里。
 */

/**
 * **本机装了哪些内容包**（id + 内容指纹）。随大厅 presence 广播，
 * 让房间页能显示"谁有哪个包"、`canStart` 能拦住不一致的开局。
 */
const LOCAL_PACKS = ALL_PACKS.map((pack) => ({ id: pack.id, hash: packHash(pack) }));

/** 本机席位的 id。整个会话里不变 —— 座次、准备状态都挂在它上面 */
function makeSelfId(): string {
  // 不是规则逻辑、不进制谱，所以这里用 `Math.random` 没有问题
  // （`core/` 的禁令是为了棋谱可复现，见 `docs/architecture.md` §4）
  return `p-${Math.random().toString(16).slice(2, 10)}`;
}

function fatal(message: string): void {
  const box = document.createElement('div');
  box.style.cssText =
    'position:fixed;inset:0;display:grid;place-items:center;padding:32px;' +
    'background:#000;color:#f43c62;font:14px ui-monospace,monospace;text-align:center';
  box.textContent = message;
  document.body.appendChild(box);
}

async function main(): Promise<void> {
  const hostElement = document.createElement('div');
  hostElement.id = 'pixi-root';
  document.body.appendChild(hostElement);

  let handle;
  try {
    handle = await createRenderer(hostElement);
  } catch (error) {
    fatal(
      t('main.rendererFatal') +
        `${error instanceof Error ? error.message : String(error)}`,
    );
    return;
  }

  // ---- 指针收在舞台上 ----
  // `hitArea = app.screen` 给的是**同一个 Rectangle 实例**，Pixi 在窗口变化时
  // 就地更新它，所以这里不需要再监听 resize（否则又是"两处各算一次"那个经典错位）
  handle.app.stage.eventMode = 'static';
  handle.app.stage.hitArea = handle.app.screen;

  // ---- 输入画像：从 PointerEvent 的属性推断 UX 行为（不设“模式”）----
  const inputProfile = createInputProfile();
  setTraitsSource(() => inputProfile.traits());

  // ---- 版面画像：方向 / 缩放 / 安全区 ----
  // ⚠️ 用户拍板：竖屏**只显示“请横屏”提示**（index.css 的 #rotate-gate），
  //    渲染锚恒为横版。这里算出的 orientation / safe 供屏幕读（安全区让位）。
  const viewportOf = (): { width: number; height: number } => ({
    width: handle.app.screen.width || window.innerWidth,
    height: handle.app.screen.height || window.innerHeight,
  });
  let layout: LayoutProfile = computeLayout(viewportOf(), readSafeAreaInsets());
  const refreshLayout = (): void => {
    const next = computeLayout(viewportOf(), readSafeAreaInsets());
    if (!sameLayout(layout, next)) layout = next;
  };
  window.addEventListener('resize', refreshLayout);
  window.visualViewport?.addEventListener('resize', refreshLayout);

  // ---- 会话状态 ----
  // 首次运行没有存过语言 → 跟随浏览器；之后以存下来的为准。
  const browserLang = detectLang(
    typeof navigator === 'undefined' ? undefined : navigator.language,
  );
  const settings = createSettingsController(
    createLocalStore(localStorage, undefined, browserLang),
    browserLang,
  );
  // ⚠️ 要在建第一屏**之前**同步语言，否则标题页会用中文字典渲染英文环境。
  setLang(settings.get().lang);

  // 竖屏提示的文案（显隐由 CSS 的 @media (orientation: portrait) 控制）
  const rotateText = document.getElementById('rotate-gate-text');
  if (rotateText !== null) rotateText.textContent = t('main.rotateHint');

  /**
   * **联机那一桌。** 房间一建/一进就连上，离开就收掉。
   *
   * ⚠️ 它是**运行时才存在**的（进房之前没有房间号，也就无所谓"连上"），
   * 所以投给界面的是个取值函数而不是一个值 —— 屏幕在**构造时**问一次，
   * 而那时它已经有答案了。
   */
  let online: OnlineRoom | null = null;

  // 房间配置的持久化：离开房间页时存 diff，下次进来自动恢复（见 ui/roomPreset.ts）
  const baseRoom = createRoomController(
    defaultRegistry,
    LOCAL_PACKS,
    createRoomPresetStore(localStorage),
  );

  /** 连接时可选的额外配置。`relayUrls` 由调用方另行给（它决定「两端用同一台」） */
  type ConnectOptions = {
    readonly turnServers?: readonly TurnServerConfig[];
    /** 信令 / 连接层报错（ICE 失败等）。只观测，不改房间状态 */
    readonly onError?: (error: unknown) => void;
  };

  /**
   * **TURN 凭据。** 开局前拉一次，缓存在内存里。
   *
   * 端点优先级：`?turn=` → localStorage `s3.turnEndpoint` → `VITE_TURN_ENDPOINT`。
   * 没配就返回空数组（只用 STUN，约 8% 的网络组合打不通）。怎么配见
   * `docs/guides/cloudflare-turn.md`。
   */
  let turnServersCache: readonly TurnServerConfig[] | null = null;
  const turnEndpoint = (): string | null => {
    try {
      const fromQuery = new URLSearchParams(window.location.search).get('turn');
      if (fromQuery !== null && fromQuery !== '') return fromQuery;
      const fromStore = window.localStorage.getItem('s3.turnEndpoint');
      if (fromStore !== null && fromStore !== '') return fromStore;
    } catch {
      // 隐私模式 / 禁了 localStorage：忽略
    }
    const fromEnv = import.meta.env.VITE_TURN_ENDPOINT;
    return fromEnv !== undefined && fromEnv !== '' ? fromEnv : null;
  };
  const resolveTurnServers = async (): Promise<readonly TurnServerConfig[]> => {
    if (turnServersCache !== null) return turnServersCache;
    const endpoint = turnEndpoint();
    if (endpoint === null) {
      turnServersCache = [];
      return turnServersCache;
    }
    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(String(response.status));
      const data: unknown = await response.json();
      const raw = (data as { iceServers?: unknown }).iceServers;
      const list = Array.isArray(raw) ? (raw as TurnServerConfig[]) : [];
      turnServersCache = list;
      console.info(t('main.logTurnConfigured'), list.length);
      return list;
    } catch (error) {
      // ⚠️ 拿不到凭据不能影响开局 —— 退化成「只有 STUN」
      console.warn(t('main.logTurnFailed'), error);
      turnServersCache = [];
      return turnServersCache;
    }
  };

  /** 连上某个房间号。重复调会先把上一条收掉 */
  const connect = (
    code: string,
    relayUrls: readonly string[] = relayUrlsForRoom(code),
    options: ConnectOptions = {},
  ): void => {
    online?.dispose();
    online = connectRoom({
      roomId: code,
      // 应用 id 是**信令上的命名空间**：同一个房间里的人一定在玩同一个游戏
      appId: 'ivory-roulette',
      // ★ 中继表显式传进去：同房两端用同一份是“能互相发现”的前提。
      //   默认值来自 resolveRelayUrls()（环境变量 / 查询参数 / localStorage / 默认表）。
      relayUrls,
      ...(options.turnServers === undefined ? {} : { turnServers: options.turnServers }),
      ...(options.onError === undefined ? {} : { onError: options.onError }),
      registry: defaultRegistry,
      snapshot: () => room.get(),
      // 我这一行 RTT —— 大厅广播会带上它，所有人拼出同一份矩阵，
      // 对局房主才选得出来（否则每个人都算出自己是房主）
      rttRow: () => online?.peer.rttMatrix()[online.selfId] ?? {},
      localPacks: () => LOCAL_PACKS,
      // ★ 版本闸门：完整 git hash，进房和房主比对。对不上的不让进
      localCommit: () => import.meta.env.VITE_COMMIT_HASH,
      apply: (next) => baseRoom.adopt(next),
      onKicked: (_by, reason) => {
        // 被创建者踢了：收摊回标题页，并把原因写进房间控制器（会显示出来）
        leaveOnline();
        baseRoom.leaveRoom();
        baseRoom.fail(reason ?? t('session.kicked'));
        host.push(SCREEN.title);
      },
      onVersionMismatch: (reason) => {
        // 房主版本对不上：不进房，退回标题页并留下原因
        leaveOnline();
        baseRoom.leaveRoom();
        baseRoom.fail(reason);
        host.push(SCREEN.title);
      },
      onStart: (seed, announcedHost, config) => {
        // 创建者宣布开局，并把**它选出来的对局房主 + 权威游戏规则**一起发出来。
        // 每个人各自开一次；创建者说的那个人优先（某个人的矩阵还没拼全时
        // 也不至于两个人各开一局）。游戏规则随行发是为了后进房的人（可能是房主）
        // 也能拿到改过的牌堆构成。
        online?.openGame(seed, announcedHost, config);
        host.push(SCREEN.game);
      },
    });
  };

  const leaveOnline = (): void => {
    online?.dispose();
    online = null;
  };

  // ============================================================
  // ★ 连接流程（"正在连接"页背后那台机器）
  // ============================================================
  //
  // 评审：「即便连不上网也能创建房间，这会误导玩家。应当加一个"正在连接"
  // 页面……若出现网络问题就返回之前的页面。」
  //
  // 相位与转移全在 `ui/connect.ts` 的纯函数里（有测试），这里只做三件它做不了
  // 的事：**探网络**（真的有 IO）、**跑计时器**（真的有时间）、
  // 以及**每次尝试算一个号**（用来作废上一尝试的回调）。
  //
  // ⚠️ `attempt` 是这里最要紧的一个字：探测是异步的，而玩家可能连点两次
  //    "创建房间"（或者探测还没回来就按了返回）。没有它的话，第一次的回调
  //    会在第二次之后落地，把人扔进一个已经放弃了的房间里。
  let connectState: ConnectState = IDLE_CONNECT;
  const connectListeners = new Set<(next: ConnectState) => void>();
  let connectAttempt = 0;
  let connectTimer: ReturnType<typeof setTimeout> | null = null;
  let connectWatch: ReturnType<typeof setInterval> | null = null;

  const publishConnect = (next: ConnectState): void => {
    connectState = next;
    for (const listener of connectListeners) listener(next);
  };
  const stepConnect = (event: Parameters<typeof reduceConnect>[1]): void => {
    publishConnect(reduceConnect(connectState, event));
  };

  const clearConnectTimers = (): void => {
    if (connectTimer !== null) clearTimeout(connectTimer);
    connectTimer = null;
    if (connectWatch !== null) clearInterval(connectWatch);
    connectWatch = null;
  };

  /** 信令探测给多久。超了就当连不上 */
  const SIGNAL_TIMEOUT_MS = 5000;
  /** 进房之后等对端应答给多久。房间号错了 / 对方不在，走的就是这条路 */
  const JOIN_TIMEOUT_MS = 15_000;

  /**
   * 走一遍完整的连接：**探中继 → 真的连 → （进房才）等对端**。
   *
   * `after` 是"连上信令之后要做的那件本机事"（建房 / 进房）——
   * 它必须在 `connect(code)` **之后**跑：大厅的席位 id 是 **peer id**，
   * 而 peer id 要等 trystero 起完才有（见下面 `createRoom` 的说明）。
   */
  const startConnect = (mode: ConnectMode, code: string, after: () => void): void => {
    const attempt = ++connectAttempt;
    const alive = (): boolean => attempt === connectAttempt;
    clearConnectTimers();
    leaveOnline();
    baseRoom.leaveRoom();
    stepConnect({ t: 'start', mode, code });

    const offline = offlineReason(navigator.onLine);
    if (offline !== null) {
      stepConnect({ t: 'unreachable', reason: offline });
      return;
    }

    // ★ 先把这次要用的中继表固定下来：探测和真正连接必须是**同一份**，
    //   否则探的是 A、连的是 B，等于白探。
    //   ★ 而且它**由房间号决定**：两端用同一个号就落到同一批中继，
    //   不再赌"两人的网络恰好对同一批有交集"（用户提的"号里携带数据"）。
    const relayUrls = relayUrlsForRoom(code);
    // ★ 连接层报错（ICE 打不通 / 握手中断）会打在这里 —— 15 秒超时时据此
    //   区分「房间号不对」和「网络连不上（可能需要 TURN）」。
    let sawConnectError = false;
    const onConnectError = (): void => {
      sawConnectError = true;
    };
    void probeRelays({ timeoutMs: SIGNAL_TIMEOUT_MS, urls: relayUrls }).then((probe) => {
      if (!alive()) return;
      if (!probe.ok) {
        // 诊断：把这个号落在哪台（或哪几台）中继打出来 —— “换个号”就是换一台。
        console.warn(t('main.logRelayProbeFailed'), code, '→', relayUrls, '｜', probe.reason);
        stepConnect({ t: 'unreachable', reason: probe.reason ?? t('connect.relayUnreachable') });
        return;
      }
      // ★ TURN 凭据（配了端点才有）先拿到再连。没配 / 拿不到就退化成只有 STUN，
      //   不阻塞开局（见 resolveTurnServers）。放在 probe 之后：中继都不通就没必要拉。
      void resolveTurnServers().then((turnServers) => {
        if (!alive()) return;
        connect(code, relayUrls, { turnServers, onError: onConnectError });
        after();
        // 诊断：把“打算用的中继”和“真的握着的 socket”都打出来。
        // 两台机器一比，就知道是不是各连各的（用户报的“互不相认”）。
        console.info(t('main.logRelayConfigured'), relayUrls, t('main.logActualSocket'), relaySocketStates());
        stepConnect({ t: 'reachable' });
        if (mode === 'create') return; // 建房间不需要等任何人

        // 进房：中继通了只说明网络通，还得**真的有人应答**。
        // 没有这条的话，随便敲一个房间号都会"成功"进到一个空房间里
        connectWatch = setInterval(() => {
          if (!alive()) {
            clearConnectTimers();
            return;
          }
          if ((online?.peer.peers().length ?? 0) > 0) {
            // ⚠️ 找到了就把两个计时器都收掉 —— 不收的话那个 15 秒的"没找到"
            //    定时器会在**连上之后**才落地，把一次成功的连接改写成 `failed`
            clearConnectTimers();
            stepConnect({ t: 'peer', count: 1 });
          }
        }, 200);
        connectTimer = setTimeout(() => {
          if (!alive()) return;
          // 诊断：找不到对端时把“本机真正活着的中继”打出来。和对面一比，
          // 若两边的 open 集合没有交集，就是各连各的（公共中继失联的典型原因）。
          console.warn(t('main.logNoPeer'), relaySocketStates());
          // ★ 期间报过连接错（典型是 ICE 打不通）就别甩锅给房间号 ——
          //   那是「网络连不上」，不是「房间不存在」。
          stepConnect({
            t: 'fail',
            reason: t(sawConnectError ? 'connect.networkUnreachable' : 'connect.roomNotFound'),
          });
        }, JOIN_TIMEOUT_MS);
      });
    });
  };

  /**
   * 收掉这次连接。
   *
   * ⚠️ `ready` 之后**不许**调它（会把刚连上的一桌收掉）—— 那条路走
   * `leaveRoom()`。正在连接页只在"还没连上"的时候喊它。
   */
  const cancelConnect = (): void => {
    connectAttempt++;
    clearConnectTimers();
    leaveOnline();
    baseRoom.leaveRoom();
    publishConnect(IDLE_CONNECT);
  };

  /**
   * 房间控制器 + 联机。
   *
   * ⚠️ 这一层包装是**组合根**的活：`RoomController` 本身刻意不认识网络
   * （它是纯大厅模型的控制器，能被单测），联机那条线在这里接上。
   */
  const room: RoomController = {
    ...baseRoom,
    /**
     * ⚠️ **先连上，再建房/进房** —— 顺序要紧。
     *
     * 席位的 id 必须是 **peer id**（评审那一句「通货是 peer id」在
     * 大厅这一层的落点）。先用本机那个随机 `p-xxxx` 建了房，就会出现
     * "名册里有两个玩家，但谁的 id 都对不上自己" —— 大厅广播回来的时候
     * `findPlayer(state, peer.selfId)` 找不到人，于是准备状态永远传不出去。
     * 这个 bug 的表现是"两边看得到对方，但准备按钮点了没反应"。
     */
    createRoom(params) {
      startConnect('create', params.code, () => {
        baseRoom.createRoom({ ...params, selfId: online?.selfId ?? params.selfId });
      });
    },
    joinRoom(params) {
      startConnect('join', params.code, () => {
        baseRoom.joinRoom({ ...params, selfId: online?.selfId ?? params.selfId });
      });
    },
    leaveRoom() {
      // 离开房间 = 这一桌结束了：连接状态也一起打回 idle
      connectAttempt++;
      clearConnectTimers();
      baseRoom.leaveRoom();
      leaveOnline();
      publishConnect(IDLE_CONNECT);
    },

    connect: () => connectState,
    subscribeConnect(listener) {
      connectListeners.add(listener);
      return () => connectListeners.delete(listener);
    },
    cancelConnect,
    // 游戏规则只有一个权威：改完就广播（别人收到会照抄，见 host/lobby.ts）
    patch(patch) {
      baseRoom.patch(patch);
      const state = baseRoom.get();
      if (state !== null && baseRoom.isCreator()) online?.broadcastConfig(state);
    },
    setDeckCount(cardId, count) {
      baseRoom.setDeckCount(cardId, count);
      const state = baseRoom.get();
      if (state !== null && baseRoom.isCreator()) online?.broadcastConfig(state);
    },
    resetDeck() {
      baseRoom.resetDeck();
      const state = baseRoom.get();
      if (state !== null && baseRoom.isCreator()) online?.broadcastConfig(state);
    },
    setReady(ready) {
      baseRoom.setReady(ready);
      online?.announce();
    },
    kick(playerId) {
      baseRoom.kick(playerId);
      const state = baseRoom.get();
      if (state !== null && baseRoom.isCreator()) online?.kick(playerId);
    },
    addBot() {
      // 在线：OnlineRoom.addBot 自己会本地落 + 广播；离线：直接改本机大厅模型
      if (online !== null) online.addBot();
      else baseRoom.addBot();
    },
  };
  const keys = new KeyRouter();
  const selfId = makeSelfId();

  // ---- BGM ----
  //
  // 评审：「将 BGM 控制模块加入游戏，尽可能早地开始播放
  // （若浏览器限制必须由玩家操作触发音乐则直接监听 window 的点击事件 once）」
  //
  // `arm()` 里做的事：**立刻**发起 load（解码最慢，且不需要手势），
  // 同时试一次 start；被自动播放策略拦下来就挂一个一次性手势。
  // 这三件事的顺序和理由写在 `ui/bgm.ts` 的文件头。
  //
  // 参数初值是 `PRE_GAME_BGM_PARAMS` —— 未开局时 isFinal/isDying 为假、
  // energy 为 0（评审要求），所以菜单里响的是大厅曲，且一层装饰都不开。
  const bgmPort = createZenithBgmPort(PRE_GAME_BGM_PARAMS);
  const bgm = createBgmController({
    port: bgmPort,
    gesture: windowGestureSource(window),
    volume: settings.get().bgmVolume,
    onError: (error) => {
      // 没有声音不该让游戏起不来 —— 记一条，继续
      console.warn(t('main.logBgmStartFailed'), error);
    },
  });

  /**
   * 音量落点：设置一改就推下去。收在这一处，设置页只管改数字。
   *
   * ⚠️ BGM 这半边现在是**真的**（`bgm.setVolume`）；音效那半边只有**打击**那
   *    一记（合成，不用素材，见 `audio/sfx.ts`）—— 别的音效还没做。
   */
  /**
   * **动态音乐：局面 → 参数。**
   *
   * 评分表在 `audio/energy.ts`（纯函数 + 一个有黏性的 tracker），
   * 这里只做两件事：把快照转成 BGM 参数、以及"回菜单时清回大厅那一套"。
   *
   * ⚠️ 菜单那一条走的是 `PRE_GAME_BGM_PARAMS` **同一个常量** ——
   * 不是"另一个恰好相等的字面量"。否则回菜单时音乐会卡在上一局。
   */
  const energyTracker = createEnergyTracker();

  /**
   * **dev 下的 BGM 覆写开关**（`DEV_TOOLS.music.override`）。
   *
   * ⚠️ 它必须在 `energyReporter` **之前**建出来：下面那两个推送点要问它
   * "锁住了没有"。锁住时评分表那条线让开 —— 否则调试时改一下参数，
   * 下一帧就被 `report()` 推回去，比没有还难用。
   *
   * 生产构建里 `createBgmOverride` 那段代码会被摇掉（它只被 dev 分支引用），
   * 但这里留一个**永远不锁**的替身，免得 `report` 里到处写
   * `if (import.meta.env.DEV)`。
   */
  const musicOverride = createBgmOverride(bgmPort, PRE_GAME_BGM_PARAMS);
  const pushParams = (params: ZenithBgmParams): void => {
    if (musicOverride.active) return;
    bgmPort.setParams(params);
  };

  /**
   * **小节时钟：音乐 → 画面。** 事件的接线在下面（`bgmPort.director.on`）。
   *
   * ⚠️ 它声明在 `energyReporter` **之前**：`reset()` 里那个"留一拍"的闩
   * 要挂 `onBeat`，而那个函数是在 `energyReporter` 的对象字面量里定义的。
   */
  const music = createMusicClock();

  /**
   * **`isDying` 暂时被压住了吗**（抽牌动画还在飞的时候）。
   *
   * ⚠️ 压住的是**报出去的那一份**，`snapshot` 不动 —— 评分表自己的判断
   * （二人局那个终局闩）照旧读真实值。见 `EnergyReporter.holdDying`。
   */
  let dyingMuted = false;
  /**
   * **压住濒死时，音乐和画面读的是同一个值。**
   *
   * 评审第二轮：「在飞到屏幕中心的动画完成后，所有濒死相关的视觉和 BGM 效果
   * 随即生效」。第五轮试过把音乐那一份提前到"抽到牌那一刻"，第六轮改回来了：
   *
   * > 我后悔了（ —— isDying 触发时间改回去，于 IMPACT 发生时生效
   *
   * 所以两个出口**共用被压住的这一份**：图和声一起等牌飞到中点。
   */
  const audible = (snapshot: EnergySnapshot): EnergySnapshot =>
    dyingMuted && snapshot.isDying ? { ...snapshot, isDying: false } : snapshot;

  const energyReporter: EnergyReporter = {
    report(facts) {
      // ⚠️ 一份新的局面**推翻**"留一拍"那个闩（见 `reset`）。权威只有一个：
      //    评分表此刻算出来的东西；闩只是"没人再报局面时"的过渡。
      //    反过来的话，一局结束后重开、或者没有音频（永远没有 beat）时，
      //    `isDying` 会永远留在真。
      endDyingHold();
      pushParams(toBgmParams(audible(energyTracker.update(facts))));
    },
    noteExceptionDraw() {
      energyTracker.noteExceptionDraw();
    },
    reset() {
      /**
       * ★ **重置时 `isDying` 先留着，过一拍再放。**
       *
       * 评审：「游戏结束重置时，`isDying` 应当先不要重置，先重置其他的 BGM 参数，
       * 仅一次监听 beat 后将 `isDying` 重置。」
       *
       * 理由听起来是这样的：濒死那一 cue 是被"戛然而止"伤害最大的一个 ——
       * 其他参数（energy、isFinal）本来就在被局面推着走，而 `isDying`
       * 从真到假是一次**切 cue**。让它跟着下一个拍点落地，
       * 那一次切换就落在音乐上，而不是落在一次状态重置上。
       *
       * 所以这里分两步：先把其他参数推成开局的 `PRE_GAME_BGM_PARAMS`、
       * 只把 `isDying` 留成真；然后**挂一个只响一次的** beat 订阅。
       */
      const wasDying = heldDying || energyTracker.snapshot.isDying;
      energyTracker.reset();
      if (!wasDying) {
        endDyingHold();
        pushParams(PRE_GAME_BGM_PARAMS);
        return;
      }
      heldDying = true;
      // 其他参数先归位（energy 0、isFinal 假），只留 isDying
      pushParams({ ...PRE_GAME_BGM_PARAMS, isDying: true });
      // ⚠️ 只挂一次：回调里第一件事就是摘掉自己（`endDyingHold`）
      if (offDyingHold === null) {
        offDyingHold = music.onBeat(() => {
          endDyingHold();
          pushParams(PRE_GAME_BGM_PARAMS);
        });
      }
    },
    get isFinal() {
      return energyTracker.snapshot.isFinal;
    },
    get energy() {
      return energyTracker.snapshot.energy;
    },
    get visualEnergy() {
      // ⚠️ 画面读的是**另一份**（濒死/终局会被抬起来）—— 见 `EnergySnapshot`
      return energyTracker.snapshot.visualEnergy;
    },
    get isDying() {
      /**
       * 闩着的时候**对所有人都是真** —— 音乐（`pushParams` 走 `audible()`）
       * 和画面（`screenHost` 每帧读这里）读的是同一个值。
       *
       * 只让一边先动的话，那半秒里图和声是错开的（试过，评审又改回来了：
       * "我后悔了（ —— isDying 触发时间改回去，于 IMPACT 发生时生效"）。
       */
      if (dyingMuted) return false;
      return heldDying || energyTracker.snapshot.isDying;
    },
    holdDying(hold) {
      if (dyingMuted === hold) return;
      dyingMuted = hold;
      // 立刻把参数推一遍：压住/放行的**那一刻**就该听出来
      pushParams(toBgmParams(audible(energyTracker.snapshot)));
    },
  };

  /**
   * **"留一拍"那个闩。**
   *
   * 只在 `reset()` 里立起来，被**第一次 beat** 或**第一份新报上来的局面**
   * 放掉（两条路都留着，见上）。所以它是一个过渡态，不是一个状态。
   */
  let heldDying = false;
  let offDyingHold: (() => void) | null = null;
  const endDyingHold = (): void => {
    if (!heldDying) return;
    heldDying = false;
    offDyingHold?.();
    offDyingHold = null;
  };

  /**
   * **拍子从哪来**（`music` 本身声明在上面）。
   *
   * 拍子**必须**由音乐自己报（`BgmDirector` 用 Tone 的 `Draw.schedule` 在音频
   * 时间轴上排回调）。用 `ticker.deltaMS` 自己数"大约一小节"是最初想过的那条路，
   * 但它会越走越偏：BPM 变了、标签页被挂起、解码晚了几百毫秒 —— 几分钟之后
   * 就是肉眼可见的错拍。所以这里只是把事件转一手。
   *
   * ⚠️ 没有音频就没有拍子，画面也就不会跟着跳（见 `ui/beat.ts` 的文件头）。
   *    这一层**故意不配**备用节拍器。
   */
  // 返回值是取消订阅。这里**故意不存**：整个页面就这一份指挥器，
  // 它和这一页同生共死，没有"晚一点摘掉"的场合
  //
  // ⚠️ 两条流都接。`bar` 给背景和濒死层，`beat` 给**终局时**的牌面脉动 ——
  //    哪一条驱动牌面由 `drivesCardPulse()` 决定，这里只负责把事件转手
  bgmPort.director.on('bar', (event) => {
    music.fireBar({ bar: event.bar, barInPhrase: event.barInPhrase });
  });
  bgmPort.director.on('beat', (event) => {
    music.fireBeat({
      bar: event.bar,
      beatInBar: event.beatInBar,
      beatsPerBar: event.beatsPerBar,
    });
  });

  /**
   * **打击音效**（低频下潜 + 噪声 crack）。合成，不需要素材 —— 项目里一个音效
   * 素材都没有，而这两样恰好是合成器最擅长的（见 `audio/sfx.ts`）。
   */
  const impactSfx = createImpactSfx();

  const audio: AudioSink = {
    setBgmVolume: (value) => bgm.setVolume(value),
    setSfxVolume: (value) => impactSfx.setVolume(value),
    // BGM 的"播放故障"滤镜 + 混响（打击那一下给药，随后衰减；不动音量）
    setBgmFx: (amount) => bgm.setFx(amount),
    // 渐强：抽牌触发濒死那一刻开始往上推，打击那一刻最大，随后快速落回
    setBgmBoost: (factor) => bgm.setBoost(factor),
    playImpact: () => impactSfx.impact(),
  };

  const applyAudio = (): void => {
    const current = settings.get();
    audio.setBgmVolume(current.bgmVolume);
    audio.setSfxVolume(current.sfxVolume);
  };
  applyAudio();
  settings.subscribe(applyAudio);
  bgm.arm();

  // ---- 菜单 ----
  /**
   * **popstate 正在把地址翻译成栈。**
   *
   * 那期间 `onStateChange` 不许再 pushState —— 否则后退一下会又压一条历史，
   * 浏览器就再也退不回去了。
   */
  let applyingRoute = false;
  const host = createScreenHost({
    world: handle.world,
    renderer: handle.renderer,
    input: handle.app.stage,
    keys,
    settings,
    room,
    audio,
    registry: defaultRegistry,
    traits: () => inputProfile.traits(),
    layout: () => layout,
    online: () => online,
    energy: energyReporter,
    music,
    selfId,
    build: {
      version: import.meta.env.VITE_APP_VERSION,
      commit: import.meta.env.VITE_COMMIT_HASH,
      channel: import.meta.env.DEV ? 'dev' : 'prod',
    },
    rootId: SCREEN.title,
    /** 栈变了就把屏幕映射成 URL（见 `ui/router.ts`） */
    onStateChange: (state) => {
      if (applyingRoute) return;
      const path = routePathFor(state);
      if (path === null) return;
      if (normalizePath(window.location.pathname) === path) return;
      window.history.pushState(null, '', path);
    },
  });

  host.define(SCREEN.title, createTitleScreen);
  host.define(SCREEN.meetup, createMeetupScreen);
  host.define(SCREEN.connecting, createConnectingScreen);
  host.define(SCREEN.room, createRoomScreen);
  // 对局屏：热座 / 联机分开两个地址，工厂也分开（见 ui/screens/game.ts）
  host.define(SCREEN.hotseat, createHotseatScreen);
  host.define(SCREEN.game, createOnlineScreen);
  host.define(SCREEN.credit, createCreditScreen);
  host.define(SCREEN.changelog, createChangelogScreen);
  host.define(OVERLAY.settings, createSettingsScreen);
  // ⚠️ 注册完才能 start —— 它才会去建第一屏
  host.start();

  // ---- ★ 屏幕 ↔ URL（含老的 #ABCD 邀请链接）----
  //
  // - 每个 screen 一个路径：`/` 标题、`/play` 汇合、`/room` 房间、`/game` 对局、
  //   `/credits` 鸣谢、`/changelog` 更新日志（见 `ui/router.ts`）。
  // - 老的邀请链接 `https://<host>/#ABCD` **照旧能用**：hash 里有合法房间号时
  //   自动进房（见 `ui/room.ts` 的 `roomJoinUrl` / `roomCodeFromHash`）。
  // - 浏览器前进后退走 `popstate` → `navigate`；那条路上 `applyingRoute` 挡住
  //   再 pushState，否则后退一下会又压一条历史。
  const joinWithCode = (code: string): void => {
    // 读完就抹掉 hash（保留子路径 / query），免得刷新或再分享时重复触发加入
    window.history.replaceState(null, '', urlWithoutHash(window.location.href));
    room.joinRoom({ code, selfId, nickname: displayNickname(settings.get()) });
    host.push(SCREEN.connecting);
  };
  const navigate = (route: Route): void => {
    applyingRoute = true;
    try {
      if (route.roomCode !== null) {
        joinWithCode(route.roomCode);
      } else if (route.screen === SCREEN.room) {
        // 直接打开 /room 而手里没有房间状态：退回汇合页（房间不能凭地址重建）
        host.go(SCREEN.meetup);
        window.history.replaceState(null, '', '/play');
      } else if (route.screen === SCREEN.game && online === null) {
        // ★ 刷新之后联机会话没了：**回退**到汇合页，而不是偷偷开一局热座。
        //   热座走自己的地址 `/hotseat`（见 createHotseatScreen）。
        host.go(SCREEN.meetup);
        window.history.replaceState(null, '', '/play');
      } else {
        host.go(route.screen);
      }
    } finally {
      applyingRoute = false;
    }
  };
  // 首次进入：按当前地址决定第一屏（`/` 就是标题页）
  navigate(parseRoute(window.location.pathname, window.location.hash));
  window.addEventListener('popstate', () => {
    navigate(parseRoute(window.location.pathname, window.location.hash));
  });

  // ---- ★ Service Worker：离线启动 ----
  //
  // 只在**构建产物**里注册（`import.meta.hot` 只有 dev server 有）——
  // dev 下注册会把 Vite 的模块缓存住，改一行代码刷新都看不到。
  if (import.meta.hot === undefined && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // 注册失败不影响游戏本身（只是没有离线能力）
      });
    });
  }

  // ---- 键盘 ----
  // 文本输入框也走这条路：它拿到焦点时往路由上压一个处理者（见 `ui/keys.ts`）
  bindKeyboard((event) => {
    // 被吃掉就拦掉默认行为 —— 否则空格会把页面滚下去、方向键会滚动画布
    if (host.handleKey(event)) event.raw?.preventDefault();
  });

  // ---- 那唯一一个 ticker ----
  handle.app.ticker.add((ticker) => {
    inputProfile.tick(performance.now());
    host.update(ticker);
  });

  // ---- ★ dev 工具：`window.DEV_TOOLS` ----
  //
  // 评审：「在 dev build 中，在 window 上附加一个 DEV_TOOLS 变量，包含一系列
  // 调试工具，比如查看内部数据，覆写 BGM 参数等。」
  //
  // ⚠️ 只在 dev 下存在。生产构建里 `import.meta.env.DEV` 是编译期常量 false，
  //    整块（连同它引用的那几个 import）都被摇掉 —— 调试后门不许进产物。
  //
  // 形状与说明在 `dev/tools.ts`；对局屏的"挂号处"在 `dev/registry.ts`。
  if (import.meta.env.DEV) {
    installDevTools({
      build: {
        version: import.meta.env.VITE_APP_VERSION,
        commit: import.meta.env.VITE_COMMIT_HASH,
      },
      selfId,
      nickname: () => displayNickname(settings.get()),
      /** 评分表的快照。老名字（冒烟脚本先叫它 `energy`），留在这一层方便敲 */
      energy: () => energyTracker.snapshot,
      screens: {
        host,
        state: () => host.state,
        go: (id) => host.go(id as Parameters<typeof host.go>[0]),
        push: (id) => host.push(id as Parameters<typeof host.push>[0]),
        pop: () => host.pop(),
        overlay: (id) => host.openOverlay(id as Parameters<typeof host.openOverlay>[0]),
        closeOverlay: (id) =>
          host.closeOverlay(id as Parameters<typeof host.closeOverlay>[0] | undefined),
      },
      settings,
      input: { traits: () => inputProfile.traits(), focus: () => host.focus().focusedId },
      room,
      net: {
        online: () => online,
        connect,
        connectState: () => connectState,
        leave: leaveOnline,
        askStealOrder: async (to: string) => {
          const session = online?.session();
          if (session === null || session === undefined) return null;
          return await session.ask(
            to,
            'steal-order',
            {},
            z.object({ order: z.array(z.number()) }),
            4000,
          );
        },
        /** ★ 当前对局 Session 的现场（房主含完整状态与动作流水）。见 net/session.ts */
        session: () => online?.session()?.debug() ?? null,
        /**
         * ★ **现在真正握着的信令中继 socket。**
         *
         * 两台机器都敲一下 DEV_TOOLS.net.relays() 对比 —— 如果各自的 open 集合
         * 没有交集，那就永远发现不了对方（用户报的“互不相认”）。
         */
        relays: () => relaySocketStates(),
      },
      music: {
        snapshot: () => energyTracker.snapshot,
        params: () => bgmPort.director.getParams(),
        /**
         * 压住 / 放行 `isDying`（抽牌动画那几百毫秒用）。
         * 冒烟要能确定性地验"压住之后音乐和画面都听不见/看不见濒死"。
         */
        holdDying: (hold: boolean) => energyReporter.holdDying(hold),
        report: (facts) => energyReporter.report(facts),
        override: musicOverride,
        clock: music,
        bgm,
        port: bgmPort,
        director: bgmPort.director,
        /**
         * **BGM 的加载读数**（评审："每次刷新页面都要重新解码 bgm"）。
         *
         * `initialMs` 是"刷新到能出声"的等待（只等当前那一个 cue），
         * `totalMs` 是后台把其余 cue 补齐用的时间，`pcmBytes` 是解出来的
         * PCM 体积 —— 也就是"空间换时间"里那个**空间**（见
         * `docs/dynamic-music.md` §6）。
         */
        load: () => bgmPort.director.loadStats,
        warm: () => bgmPort.director.warmPromise,
      },
      game: {
        current: () => currentGame(),
        view: () => currentGame()?.view() ?? null,
        options: () => currentGame()?.options() ?? [],
        act: (action) => currentGame()?.act(action as GameAction) ?? null,
        effects: () => currentGame()?.effects?.() ?? null,
        buttons: () => currentGame()?.buttons?.() ?? null,
        /** ★ 权威状态：热座 / 联机房主；客户端是 null */
        state: () => currentGame()?.state?.() ?? null,
        /** ★ 桌面时序现场（窗口 / 定时器 / 举着的牌 / 手牌可不可打） */
        table: () => currentGame()?.table?.() ?? null,
        /** ★ 这一局的 Session 现场；对局屏没挂号时退回 online */
        session: () => currentGame()?.session?.() ?? online?.session()?.debug() ?? null,
      },
      render: {
        renderer: handle.renderer,
        world: handle.world,
        mood: () => host.mood(),
        dying: () => host.dying(),
        field: () => host.field(),
        quad: () => host.quad(),
        glitch: () => host.glitch(),
        impact: () => host.impact(),
        /**
         * 裂缝遮罩的现场读数。
         *
         * ⚠️ `covered` 是**数像素数出来的**：裂缝是"标量场的一块遮罩"，
         * 而它坏掉的样子（`setDying` 每帧重开）恰好是"强度是 1、贴图是空的"。
         */
        cracks: () => {
          const mask = host.cracks();
          if (mask === null) return null;
          const { pixels } = handle.renderer.extract.pixels(mask.mask);
          let covered = 0;
          for (let i = 0; i < pixels.length; i += 4) {
            if ((pixels[i + 3] ?? 0) > 128) covered += 1;
          }
          return { strength: mask.strength, live: mask.live, arms: mask.armCount, covered };
        },
      },
      /**
       * ★ **权威那一侧的现场。** 热座来自 HotSeat，联机来自房主 Session。
       * 客户端 / 菜单里 state 是 null，但 table（对局屏现场）仍然有。
       */
      host: () => ({
        state: currentGame()?.state?.() ?? online?.session()?.debug()?.state ?? null,
        table: currentGame()?.table?.() ?? null,
        session: currentGame()?.session?.() ?? online?.session()?.debug() ?? null,
      }),
      dump: () => ({
        build: { version: import.meta.env.VITE_APP_VERSION, commit: import.meta.env.VITE_COMMIT_HASH },
        stack: host.state,
        room: room.get(),
        settings: settings.get(),
        music: bgmPort.director.getParams(),
        energy: energyTracker.snapshot,
        mood: host.mood(),
        online:
          online === null
            ? null
            : {
                selfId: online.selfId,
                code: room.get()?.code ?? null,
                host: online.pickGameHost(),
                peers: online.peer.peers(),
                rtt: online.rtt(),
                relays: relaySocketStates(),
              },
        game: currentGame()?.label ?? null,
        view: currentGame()?.view() ?? null,
        options: currentGame()?.options() ?? null,
        host: currentGame()?.state?.() ?? null,
        table: currentGame()?.table?.() ?? null,
        session: currentGame()?.session?.() ?? online?.session()?.debug() ?? null,
      }),
    });
  }
}

void main();
